import { createTheme, responsiveFontSizes } from '@mui/material';
import { ThemeOptions } from '@mui/material/styles';

export const colors = {
  evp_primary: '#363CFF',
  evp_blue_200: '#A3BBFF',
  primary: '#0ea5e9',
  grey: '#6C757D',
  white: '#FFFFFF',
  white_accent: '#F7FAFC',
  dark_grey: '#333333',
  black: '#212529',
};

// Components have their light/dark properties as props so it is easier to handle more complex cases
// There are 2 booleans that are opposed to each other so that comparisons can be done as isLight? or isDark?
type componentProps = (isLight: boolean, isDark: boolean) => ThemeOptions;
const components: componentProps = (isLight, isDark) => {
  return {
    components: {
      MuiButton: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
          disableElevation: true,
          variant: 'contained',
        },
        styleOverrides: {
          root: {
            borderRadius: '4px !important',
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 24,
            padding: 8,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: colors.primary,
            },
          },
        },
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
      },
      MuiCard: {
        defaultProps: {
          variant: 'elevation',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            outline: 'none',
            border: 'none',
            borderRadius: '4px',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            fontSize: '0.875rem',
            lineHeight: 1.25,
            fontWeight: 700,
            height: '2.25rem',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: colors.evp_primary,
          },
          bar: {
            backgroundColor: colors.evp_blue_200,
          },
        },
      },
    },
  };
};

let theme = createTheme({
  shape: {
    borderRadius: 4,
  },
  typography: {
    fontFamily:
      'Nunito Sans, ui-sans-serif, system-ui, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
    h1: { fontSize: '1.875rem', fontWeight: 600, lineHeight: 1.25 },
    h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25 },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.25 },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25 },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.25 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.25 },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },

  ...components(true, false),
});

//Custom colors

theme = createTheme(theme, {
  // Custom colors created with augmentColor go here
  palette: {
    mode: 'light',
    primary: theme.palette.augmentColor({
      color: {
        main: colors.primary,
      },
      name: 'primary',
    }),
    secondary: theme.palette.augmentColor({
      color: {
        main: colors.grey,
      },
      name: 'secondary',
    }),
    white: theme.palette.augmentColor({
      color: {
        main: colors.white,
      },
      name: 'white',
    }),
    accentWhite: theme.palette.augmentColor({
      color: {
        main: colors.white_accent,
      },
      name: 'accentWhite',
    }),
    lightGrey: theme.palette.augmentColor({
      color: {
        main: colors.grey,
      },
      name: 'lightGrey',
    }),
    background: {
      default: colors.white,
      paper: colors.white_accent,
    },
  },
});

//Module augmentation

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    white: Palette['primary'];
    accentWhite: Palette['primary'];
    lightGrey: Palette['primary'];
  }
  interface PaletteOptions {
    white: PaletteOptions['primary'];
    accentWhite: PaletteOptions['primary'];
    lightGrey: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white: true;
    accentWhite: true;
    lightGrey: true;
  }
}

// Make the themes responsive using responsiveFontSizes
export const lightTheme = responsiveFontSizes(theme);
export const darkTheme = responsiveFontSizes(theme);
